// src/pages/Dashboard/Admin/Parametres/components/UploadTrashMaintenanceCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaBroom,
  FaExclamationTriangle,
  FaFolderOpen,
  FaRecycle,
  FaSyncAlt,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  emptyUploadsTrash,
  getUploadsTrashReport,
} from "../../../../../../services/uploadMaintenanceServices";

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);

  if (value < 1024) return `${value} o`;

  if (value < 1024 * 1024) {
    return `${(value / 1024).toLocaleString("fr-FR", {
      maximumFractionDigits: 1,
    })} Ko`;
  }

  return `${(value / 1024 / 1024).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  })} Mo`;
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function UploadTrashMaintenanceCard({
  open,
  onClose,
  onTrashEmptied,
}) {
  const [trashReport, setTrashReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emptying, setEmptying] = useState(false);

  const files = trashReport?.files || [];
  const counts = trashReport?.counts || {};

  const lastFiles = useMemo(() => {
    return files.slice(0, 12);
  }, [files]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.documentElement.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    loadTrashReport({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTrashReport = async ({ silent = false } = {}) => {
    try {
      setLoading(true);

      const data = await getUploadsTrashReport();
      setTrashReport(data);

      if (!silent) {
        toast.success("Corbeille analysée.");
      }
    } catch (error) {
      console.error("[UploadTrashMaintenanceCard] load error:", error);

      toast.error(
        error?.message || "Impossible de charger la corbeille des fichiers."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmptyTrash = async (olderThanDays = 0) => {
    if (!files.length) {
      toast.info("La corbeille est déjà vide.");
      return;
    }

    const message =
      olderThanDays > 0
        ? `Supprimer définitivement les fichiers en corbeille depuis plus de ${olderThanDays} jours ?`
        : `Supprimer définitivement les ${files.length} fichier(s) de la corbeille ?`;

    const confirmed = window.confirm(
      `${message}\n\nCette action est irréversible.`
    );

    if (!confirmed) return;

    const security = window.prompt(
      "Pour confirmer la suppression définitive, tapez : SUPPRIMER"
    );

    if (security !== "SUPPRIMER") {
      toast.info("Suppression annulée.");
      return;
    }

    try {
      setEmptying(true);

      const result = await emptyUploadsTrash({ olderThanDays });

      const deleted = result?.summary?.deleted || 0;
      const errors = result?.summary?.errors || 0;

      if (deleted > 0) {
        toast.success(`${deleted} fichier(s) supprimé(s) définitivement.`);
      } else {
        toast.info("Aucun fichier supprimé.");
      }

      if (errors > 0) {
        toast.error(`${errors} erreur(s) pendant la suppression.`);
      }

      await loadTrashReport({ silent: true });
      onTrashEmptied?.();
    } catch (error) {
      console.error("[UploadTrashMaintenanceCard] empty error:", error);

      toast.error(
        error?.message || "Impossible de vider définitivement la corbeille."
      );
    } finally {
      setEmptying(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Fermer la corbeille"
      />

      <aside
        className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950"
        role="dialog"
        aria-modal="true"
      >
        <div className="h-1.5 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400" />

        <header className="shrink-0 border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Suppression définitive
              </p>

              <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
                <FaRecycle className="text-rose-500" />
                Corbeille des fichiers
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                Les fichiers déplacés en corbeille ne sont plus utilisés par
                l’application. Vous pouvez les supprimer définitivement ici.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">
              {loading
                ? "Analyse de la corbeille en cours..."
                : trashReport
                  ? `${counts.files || 0} fichier(s) — ${formatBytes(
                      counts.totalBytes || 0
                    )}`
                  : "Corbeille non analysée"}
            </div>

            <button
              type="button"
              onClick={() => loadTrashReport()}
              disabled={loading || emptying}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              {loading ? "Analyse..." : "Actualiser"}
            </button>
          </div>

          {trashReport ? (
            <>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <StatCard
                  label="Fichiers"
                  value={counts.files || 0}
                  icon={<FaFolderOpen />}
                />

                <StatCard
                  label="Taille totale"
                  value={formatBytes(counts.totalBytes || 0)}
                  icon={<FaBroom />}
                />

                <StatCard
                  label="Statut"
                  value={(counts.files || 0) > 0 ? "À vider" : "Vide"}
                  icon={<FaExclamationTriangle />}
                  tone={(counts.files || 0) > 0 ? "rose" : "emerald"}
                />
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleEmptyTrash(0)}
                  disabled={!files.length || emptying}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaTrashAlt className={emptying ? "animate-pulse" : ""} />
                  {emptying ? "Suppression..." : "Vider toute la corbeille"}
                </button>

                <button
                  type="button"
                  onClick={() => handleEmptyTrash(30)}
                  disabled={!files.length || emptying}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Supprimer les fichiers de +30 jours
                </button>
              </div>

              {files.length > 0 ? (
                <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      Derniers fichiers en corbeille
                    </p>
                  </div>

                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {lastFiles.map((file) => (
                      <div
                        key={file.path}
                        className="grid gap-2 px-4 py-3 text-sm md:grid-cols-[1fr_120px_160px]"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-bold text-slate-800 dark:text-slate-100">
                            {file.filename}
                          </p>

                          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                            {file.path}
                          </p>
                        </div>

                        <div className="text-xs font-bold text-slate-600 dark:text-slate-300">
                          {formatBytes(file.size)}
                        </div>

                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDate(file.modifiedAt)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {files.length > lastFiles.length ? (
                    <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                      +{files.length - lastFiles.length} autre(s) fichier(s) en
                      corbeille.
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Corbeille vide
                  </p>

                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Aucun fichier à supprimer définitivement.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
              <FaRecycle className="mx-auto text-3xl text-slate-400" />

              <p className="mt-4 font-bold text-slate-900 dark:text-white">
                Corbeille non analysée
              </p>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Lancez l’analyse pour connaître les fichiers en attente de
                suppression définitive.
              </p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function StatCard({ label, value, icon, tone = "indigo" }) {
  const tones = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
  };

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-lg dark:bg-slate-950/60">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-70">
            {label}
          </p>

          <p className="mt-0.5 break-words text-lg font-bold leading-tight text-slate-950 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}